/* import __COLOCATED_TEMPLATE__ from './new-export-schedule-side-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-reporting */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Store from '@ember-data/store';
import { type ExportRule, SchedulePreset } from 'embercom/models/reporting/dataset-export-schedule';
import type DatasetExportSchedule from 'embercom/models/reporting/dataset-export-schedule';
import { isEmpty } from 'underscore';
import type DatasetExportScheduleService from 'embercom/services/dataset-export-schedule-service';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';

interface Args {
  onClose: () => void;
  isOpen: boolean;
  schedule: DatasetExportSchedule;
  header: string;
}

export default class NewExportScheduleSideDrawer extends Component<Args> {
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare datasetExportScheduleService: DatasetExportScheduleService;
  @service notificationsService: any;

  @tracked selectedFrequencyIndex = this.initialSelectedFrequencyIndex;

  get disableSaveButton() {
    return isEmpty(this.schedule.name);
  }

  get schedule() {
    return this.args.schedule;
  }

  @action
  scheduleExport() {
    this.save();
    this.args.onClose();
  }

  async save() {
    try {
      let savedSchedule = await this.schedule.save();

      this.notificationsService.notifyConfirmation(
        this.intl.t('reporting.data-export.schedule-export.new-schedule.success-message', {
          deliveryTime: savedSchedule.formattedDeliveryAt,
        }),
      );
    } catch {
      this.notificationsService.notifyError(
        this.intl.t('reporting.data-export.schedule-export.new-schedule.error-message'),
      );
    }
  }

  get weekDays() {
    return [
      this.intl.t('app.lib.reporting.week-days.sunday'),
      this.intl.t('app.lib.reporting.week-days.monday'),
      this.intl.t('app.lib.reporting.week-days.tuesday'),
      this.intl.t('app.lib.reporting.week-days.wednesday'),
      this.intl.t('app.lib.reporting.week-days.thursday'),
      this.intl.t('app.lib.reporting.week-days.friday'),
      this.intl.t('app.lib.reporting.week-days.saturday'),
    ];
  }

  get currentDayOfWeekLabel() {
    return this.weekDays[this.currentDayOfWeek];
  }

  get currentDayOfWeek() {
    return new Date().getDay();
  }

  get timezone() {
    return this.schedule.timezone;
  }

  presetExportRule(preset: SchedulePreset): ExportRule {
    return this.datasetExportScheduleService.exportRuleFrom(preset, this.timezone);
  }

  @action
  updateSelection(preset: SchedulePreset) {
    this.schedule.updateExportRule(this.presetExportRule(preset));
  }

  get selectedPresetLabel() {
    return this.datasetExportScheduleService.customLabel(this.schedule);
  }

  @action
  updateSelectedFrequencyValue(index: number) {
    this.selectedFrequencyIndex = index;
  }

  get initialSelectedFrequencyIndex() {
    let index = this.frequencyItems.findIndex((item) => item.text === this.selectedPresetLabel);
    return index === -1 ? 3 : index;
  }

  get frequencyItems() {
    return [
      {
        text: this.intl.t(
          'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.daily',
        ),
        value: SchedulePreset.DAILY,
        onSelectItem: () => {
          this.updateSelection(SchedulePreset.DAILY);
        },
      },
      {
        text: this.intl.t(
          'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.weekly',
          {
            dayOfWeek: this.currentDayOfWeekLabel,
          },
        ),
        value: SchedulePreset.WEEKLY,
        onSelectItem: () => {
          this.updateSelection(SchedulePreset.WEEKLY);
        },
      },
      {
        text: this.intl.t(
          'reporting.data-export.schedule-export.new-schedule.frequency-dropdown.monthly',
          {
            dayOfWeek: this.currentDayOfWeekLabel,
          },
        ),
        value: SchedulePreset.MONTHLY,
        onSelectItem: () => {
          this.updateSelection(SchedulePreset.MONTHLY);
        },
      },
    ];
  }

  @action
  onClose() {
    if (this.schedule.get('isNew')) {
      this.schedule.deleteRecord();
    } else {
      this.schedule.rollbackAttributes();
    }
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::NewExportScheduleSideDrawer': typeof NewExportScheduleSideDrawer;
    'reporting/new-export-schedule-side-drawer': typeof NewExportScheduleSideDrawer;
  }
}
